import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, ParamMap, Router, UrlSegment} from "@angular/router";
import {combineLatest, Observable, of} from "rxjs";
import {switchMap, take, tap} from "rxjs/operators";
import {DocumentsV3RestService} from "../../services/rest/v3/documents-rest.service";
import {File, Folder, RepoEntry, RepoEntryType} from "../../model/v3/documents";


@Component({
    selector: 'app-documents-browser',
    templateUrl: './documents-browser.component.html',
    styleUrls: ['./documents-browser.component.scss'],
    standalone: false
})
export class DocumentsBrowserComponent implements OnInit {
  schoolId: number;

  constructor(
    private activatedRoute: ActivatedRoute,
    private documentsV3Rest: DocumentsV3RestService,
    private router: Router
    ) {
    combineLatest(
      [activatedRoute.url,
            activatedRoute.paramMap,
            activatedRoute.queryParamMap
            ]
    ).pipe(
      tap<[UrlSegment[], ParamMap, ParamMap]>(([_, params, _2]) => this.schoolId = +params.get("schoolId")),
      switchMap(([url, params, queryParams]) =>
        combineLatest([
          of(this.buildDocumentPath(url)),
          this.obtainData(params, !this.isDownloadAction(queryParams)),
          of(this.isDownloadAction(queryParams))
        ])),
      tap<[string[], RepoEntry, boolean]>( ([documentPath, repoData, downloadIt]) =>
        this.prepareView(repoData, documentPath, downloadIt))
    )
      .subscribe()
  }

  ngOnInit(): void {
  }

  documentRoot: RepoEntry;
  documentsPath: RepoEntry[] = [];

  private buildDocumentPath(segments: UrlSegment[]): string[] {
    if (segments.length === 0) return [];
    return segments.map(segment => segment.path);
  }

  private isDownloadAction(queryParams: ParamMap): boolean {
    return queryParams.has("download");
  }

  private obtainData(params: ParamMap, reloadIfEmpty: boolean): Observable<RepoEntry> {
    if (!reloadIfEmpty) return of(this.documentRoot);
    if (this.documentRoot) return of(this.documentRoot);
    return this.documentsV3Rest.listDocumentRepository(Number(params.get("schoolId"))).pipe(
      tap(documentRoot => this.documentRoot = documentRoot)
    );
  }

  private prepareView(repoData: RepoEntry, documentPath: string[], downloadIt: boolean) {
    if (downloadIt) {
      this.documentsV3Rest.getDocumentLink(this.schoolId, documentPath.join("/")).subscribe(
        {
          next: link => location.href = link,
          error: _ => this.routeToNotFound()
        });
      return;
    }
    // if path is empty redirect to the correct folder
    if (documentPath.length === 0) {
      this.router.navigate([repoData.relativeKey], {relativeTo: this.activatedRoute}).then();
      return;
    }

    this.documentsPath = [];
    let currentItemKids: RepoEntry[] = [repoData];
    for (const pathSegment of documentPath) {
      const nextItem = currentItemKids.find(item => item.relativeKey === pathSegment)
      if (!nextItem) {
        this.routeToNotFound();
        return;
      }
      this.documentsPath.push(nextItem)
      currentItemKids = (nextItem as Folder).innerEntries;
    }
  }

  private routeToNotFound() {
    this.router.navigate(["404"]).then();
  }

  last() {
    if (this.documentsPath.length == 0) return null;
    return this.documentsPath[this.documentsPath.length - 1];
  }

  lastAsDir() {
    if (this.isDir(this.last())) return this.last() as Folder;
    return null;
  }

  isDir(item: RepoEntry) {
    if (!item) return false;
    return item["@type"] === RepoEntryType.Dir;
  }

  download(item: RepoEntry) {
    this.documentsV3Rest.getDocumentLink(this.schoolId, item.key).subscribe(
      {
        next: link => window.open(link, "_blank"),
        error: _ => this.routeToNotFound()
      });
  }

  up() {
    this.router.navigate([".."], {relativeTo: this.activatedRoute}).then()
  }

  getSize(item: RepoEntry) {
    if (item["@type"] === RepoEntryType.File) {
      let size = (item as File).size;
      if(!size) return ""
      if (size < 1024) return size.toString();
      size = Math.round(size / 1024);
      if (size < 1024) return `${size} KB`;
      size = Math.round(size / 1024);
      return `${size} MB`;
    }
    return "";
  }

  refresh() {
    combineLatest([
      this.documentsV3Rest.refreshDocuments(this.schoolId).pipe(
        switchMap(_ => this.documentsV3Rest.listDocumentRepository(this.schoolId)),
        tap<RepoEntry>(documentRoot => this.documentRoot = documentRoot)
      ),
      this.activatedRoute.url
    ]).pipe(
      take(1),
      tap<[RepoEntry, UrlSegment[]]>( ([repoData,url]) => this.prepareView(repoData, this.buildDocumentPath(url), false)) ).subscribe()
  }
}
